import { useEffect } from "react";

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
    el.setAttribute(k, v.replace(/"/g, ""));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href, hreflang) => {
  const sel = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const SEOHead = ({ lang }) => {
  useEffect(() => {
    const isEs = lang === "es";
    const title = isEs
      ? "Bendito Tattoo | Estudio de Tatuajes y Piercings en Playa del Carmen, México"
      : "Bendito Tattoo | Tattoo & Piercing Studio in Playa del Carmen, Mexico";
    const desc = isEs
      ? "Tatuajes personalizados, piercings y modificaciones corporales por artistas expertos en Playa del Carmen. Línea fina, micro realismo, blackwork y más. Reserva tu consulta gratis por WhatsApp."
      : "Custom tattoos, piercings & body mods by expert artists in Playa del Carmen. Fine line, micro realism, blackwork & more. Book your free consultation on WhatsApp.";

    const origin = "https://benditotattoo.com";
    const pageUrl = `${origin}/${isEs ? "es" : "en"}`;
    const ogImage = `${origin}/images/logo/logo.jpeg`;

    document.title = title;
    document.documentElement.setAttribute("lang", isEs ? "es" : "en");

    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:site_name"]', "content", "Bendito Tattoo");
    setMeta('meta[property="og:locale"]', "content", isEs ? "es_MX" : "en_US");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", ogImage);
    setMeta('meta[name="google-site-verification"]', "content", "ly887FWAfnrf6sANaZ641d9PrpFkEWKfMI3TjOf_9bg");

    // hreflang
    setLink("alternate", `${origin}/en`, "en");
    setLink("alternate", `${origin}/es`, "es");
    setLink("alternate", `${origin}/en`, "x-default");
    setLink("canonical", pageUrl);

    // JSON-LD: TattooParlor (comprehensive)
    const ldId = "ld-tattooparlor";
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement("script");
      ld.id = ldId;
      ld.type = "application/ld+json";
      document.head.appendChild(ld);
    }
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TattooParlor",
      "@id": `${origin}/#tattooparlor`,
      name: "Bendito Tattoo",
      description: isEs
        ? "Estudio de tatuajes, piercings y modificaciones corporales en Playa del Carmen. Línea fina, micro realismo, blackwork, tribal y maorí."
        : "Tattoo, piercing & body modification studio in Playa del Carmen. Fine line, micro realism, blackwork, tribal & Maori.",
      image: ogImage,
      logo: ogImage,
      url: origin,
      telephone: "+529987635079",
      email: "thebenditotattoo@gmail.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Playa del Carmen Centro",
        addressLocality: "Playa del Carmen",
        addressRegion: "Quintana Roo",
        postalCode: "77710",
        addressCountry: "MX",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 20.6296,
        longitude: -87.0739,
      },
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "10:00",
        closes: "00:00",
      },
      priceRange: "$$",
      availableLanguage: [
        { "@type": "Language", name: "English", alternateName: "en" },
        { "@type": "Language", name: "Spanish", alternateName: "es" },
        { "@type": "Language", name: "Italian", alternateName: "it" },
      ],
      paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
      currenciesAccepted: ["MXN", "USD"],
      sameAs: ["https://www.instagram.com/benditotattoo.pdc/"],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Tattoo & Piercing Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Custom Tattoo" },
          },
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Piercing" },
          },
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Body Modification" },
          },
        ],
      },
      employee: [
        {
          "@type": "Person",
          name: "EEME",
          jobTitle: "Tattoo Artist",
          description: "Specializes in fine line and micro realism tattoos",
        },
        {
          "@type": "Person",
          name: "May",
          jobTitle: "Tattoo Artist",
          description: "Specializes in fine line and micro realism tattoos",
        },
        {
          "@type": "Person",
          name: "Tyson",
          jobTitle: "Tattoo Artist & Piercer",
          description: "Specializes in blackwork, geometric, tribal, Maori and piercings",
        },
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: "47",
        bestRating: "5",
        worstRating: "1",
      },
      review: [
        {
          "@type": "Review",
          author: { "@type": "Person", name: "Sarah M." },
          reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
          reviewBody: "Amazing experience! EEME did an incredible fine line piece on my forearm. Clean studio, professional artists.",
        },
        {
          "@type": "Review",
          author: { "@type": "Person", name: "Carlos R." },
          reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
          reviewBody: "Best tattoo studio in Playa del Carmen. Got a Mayan design that blew my mind. Highly recommend.",
        },
      ],
      areaServed: {
        "@type": "City",
        name: "Playa del Carmen",
        containedInPlace: {
          "@type": "State",
          name: "Quintana Roo",
          containedInPlace: { "@type": "Country", name: "Mexico" },
        },
      },
    });
  }, [lang]);
  return null;
};

export default SEOHead;
